// Architecture-topology model.
//
// This is the data behind the isometric network diagram. It is *derived* from
// the captured inventory (see `buildTopology.ts`) but is deliberately a
// first-class document: the auto-build is a best-effort read of a product
// inventory, and an inventory does not tell you how a network is wired.
//
// SELF-CONTAINED verbatim port from the example app's modules/topology/types.ts.

/** The visual vocabulary. Each kind maps to one topology glyph drawn in the
 *  Cisco network-topology idiom (arrowed pucks for routers, arrowed boxes for
 *  switches, brick walls for firewalls, …). */
export type TopoNodeKind =
  // Tier: cloud
  | "internet"
  | "cloud"
  | "cloud_app"
  | "sase"
  | "cloud_mgmt"
  // Tier: edge
  | "router"
  | "sdwan_edge"
  | "sp_router"
  | "firewall"
  | "identity"
  | "shield"
  // Tier: core
  | "l3_switch"
  | "switch"
  | "optics"
  // Tier: data centre
  | "dc_switch"
  | "server"
  | "storage"
  // Tier: access
  | "wlc"
  | "ap"
  | "iot_switch"
  | "iot_router"
  // Tier: endpoint / ops
  | "phone"
  | "video"
  | "camera"
  | "iot"
  | "analytics"
  | "monitor"
  | "ai_agent"
  | "generic";

export const TOPO_NODE_KINDS: TopoNodeKind[] = [
  "internet", "cloud", "cloud_app", "sase", "cloud_mgmt",
  "router", "sdwan_edge", "sp_router", "firewall", "identity", "shield",
  "l3_switch", "switch", "optics",
  "dc_switch", "server", "storage",
  "wlc", "ap", "iot_switch", "iot_router",
  "phone", "video", "camera", "iot", "analytics", "monitor", "ai_agent", "generic",
];

export const TOPO_KIND_LABEL: Record<TopoNodeKind, string> = {
  internet: "Internet",
  cloud: "Cloud",
  cloud_app: "Cloud service",
  sase: "SASE / SSE",
  cloud_mgmt: "Cloud management",
  router: "Router",
  sdwan_edge: "SD-WAN edge",
  sp_router: "Service-provider router",
  firewall: "Firewall",
  identity: "Identity / NAC",
  shield: "Threat detection",
  l3_switch: "Layer-3 switch",
  switch: "Switch",
  optics: "Optics / transport",
  dc_switch: "Data-centre switch",
  server: "Server / compute",
  storage: "Storage / SAN",
  wlc: "Wireless controller",
  ap: "Access point",
  iot_switch: "Industrial switch",
  iot_router: "Industrial router",
  phone: "Voice endpoint",
  video: "Video endpoint",
  camera: "Camera",
  iot: "IoT sensor",
  analytics: "Analytics / SIEM",
  monitor: "Assurance / monitoring",
  ai_agent: "AI agent",
  generic: "Other",
};

/** Horizontal bands of the diagram, top (north-west) to bottom (south-east). */
export type TopoTierId =
  | "cloud"
  | "edge"
  | "core"
  | "dc"
  | "access"
  | "endpoint"
  | "ops";

export const TOPO_TIERS: { id: TopoTierId; label: string; short: string; blurb: string }[] = [
  { id: "cloud", label: "Internet & cloud", short: "INTERNET & CLOUD", blurb: "Everything outside your administrative control." },
  { id: "edge", label: "WAN edge & perimeter", short: "WAN EDGE", blurb: "Where external traffic is inspected and policy is applied." },
  { id: "core", label: "Core & aggregation", short: "CORE", blurb: "The high-throughput spine the rest of the estate hangs off." },
  { id: "dc", label: "Data centre & compute", short: "DATA CENTRE", blurb: "Where workloads and data actually live." },
  { id: "access", label: "Campus access & wireless", short: "ACCESS & WIRELESS", blurb: "Where users and devices attach to the network." },
  { id: "endpoint", label: "Endpoints & things", short: "ENDPOINTS", blurb: "Voice, video, cameras, sensors, agents." },
  { id: "ops", label: "Operations & telemetry", short: "OPS & TELEMETRY", blurb: "The tooling that observes everything above." },
];

export const TOPO_TIER_INDEX: Record<TopoTierId, number> = Object.fromEntries(
  TOPO_TIERS.map((t, i) => [t.id, i]),
) as Record<TopoTierId, number>;

export function topoTierLabel(id: TopoTierId): string {
  return TOPO_TIERS.find((t) => t.id === id)?.label ?? id;
}

/** The caption used on the diagram itself. Short on purpose: a caption hangs
 *  off the left of its band, and the full names push the whole scene right by
 *  a couple of grid cells for no added meaning — the legend under the canvas
 *  spells them out. */
export function topoTierCaption(id: TopoTierId): string {
  return TOPO_TIERS.find((t) => t.id === id)?.short ?? id.toUpperCase();
}

export type TopoNode = {
  id: string;
  label: string;
  kind: TopoNodeKind;
  tier: TopoTierId;
  /** Isometric grid coordinates. Integers; the renderer projects them. */
  col: number;
  row: number;
  /** Entity item ids folded into this node — the audit trail back to the
   *  upload, so an edited diagram can still be traced to its source. */
  sourceItemIds?: string[];
  /** Set when the node was added or edited by hand rather than derived. */
  manual?: boolean;
  /** Carried through from the catalog's EoL flag. */
  legacy?: boolean;
  note?: string;
};

export type TopoEdgeKind = "uplink" | "wan" | "trunk" | "mgmt";

export const TOPO_EDGE_KINDS: TopoEdgeKind[] = ["wan", "uplink", "trunk", "mgmt"];

/** What each link type claims, in the reader's words, and the colour it is
 *  drawn in. Colour encodes exactly one thing: the kind of link. The legend
 *  and the canvas both read from here. */
export const TOPO_EDGE_KIND_META: Record<
  TopoEdgeKind,
  { label: string; meaning: string; color: string; dash?: string; opacity: number }
> = {
  wan: {
    label: "WAN / internet",
    meaning: "Traffic leaving your administrative control, or arriving from outside it.",
    color: "var(--chart-purple)",
    dash: "10 7",
    opacity: 0.9,
  },
  uplink: {
    label: "Uplink",
    meaning: "A routed hop further into the estate — perimeter to core, core to data centre.",
    color: "var(--cisco-blue)",
    opacity: 0.9,
  },
  trunk: {
    label: "Trunk / access",
    meaning: "Switched aggregation — core to access, access to the things attached to it.",
    color: "var(--chart-light-blue)",
    opacity: 0.75,
  },
  mgmt: {
    label: "Management",
    meaning: "Observes or configures the estate; not a path production traffic takes.",
    color: "var(--ink-faint)",
    dash: "2 6",
    opacity: 0.6,
  },
};

export type TopoEdge = {
  id: string;
  from: string;
  to: string;
  kind: TopoEdgeKind;
  manual?: boolean;
};

export type TopoModel = {
  /** Bumped whenever `buildTopology.ts`'s output shape changes so a persisted,
   *  never edited model can be regenerated instead of rendering stale. */
  version: number;
  nodes: TopoNode[];
  edges: TopoEdge[];
  /** True once the user has touched it — the report then stops labelling it
   *  "auto-generated, unverified". */
  edited?: boolean;
};

export const TOPO_MODEL_VERSION = 1;

export function emptyTopology(): TopoModel {
  return { version: TOPO_MODEL_VERSION, nodes: [], edges: [] };
}

export function edgeId(from: string, to: string): string {
  return `${from}->${to}`;
}
